import type { ShapeData } from '@/lib/shapes/types/shape-data'
import type { EffectSelection } from './types/effect-selection'
import type { EffectSelectionOptions } from './types/effect-selection-options'

/**
 * Shared selection model for the numeric Object Effects controls (Sides, Inset, Rounding).
 *
 * Every one of those controls follows the same rules for a multi-selection:
 *
 * - the control is enabled when **any** selected object supports the effect;
 * - a commit applies to **every** eligible object and silently passes over the rest;
 * - the numeric field shows the shared committed value, or blank when eligible objects disagree
 *   (showing the first object's value would read as "all of them are this").
 *
 * Keeping this in one place stops the three panels from re-deriving — and drifting on — the rules.
 */

/** Field text for a mixed selection: blank, never a representative number. */
export const MIXED_VALUE_INPUT_TEXT = ''

function resolveLiveShape(
  shape: ShapeData,
  liveShapes: readonly ShapeData[] | undefined
): ShapeData {
  if (!liveShapes) return shape

  return liveShapes.find((candidate) => candidate.id === shape.id) ?? shape
}

export function resolveEffectSelection(
  selected: readonly ShapeData[],
  opts: EffectSelectionOptions
): EffectSelection {
  const { isEligible, readValue, fallbackValue, liveShapes } = opts
  const eligible: ShapeData[] = []
  const skipped: ShapeData[] = []

  for (const shape of selected) {
    if (isEligible(shape)) {
      eligible.push(shape)
    } else {
      skipped.push(shape)
    }
  }

  const values = eligible.map((shape) => readValue(resolveLiveShape(shape, liveShapes)))
  const first = values[0] ?? fallbackValue
  const allSame = values.every((value) => value === first)

  return {
    selected: [...selected],
    eligible,
    skipped,
    hasEligible: eligible.length > 0,
    ineligible: selected.length > 0 && eligible.length === 0,
    commonValue: values.length === 0 ? fallbackValue : allSame ? first : null,
    sliderValue: first,
  }
}

/** Field text for a resolved selection: blank when eligible shapes disagree. */
export function formatEffectInputText(commonValue: number | null): string {
  return commonValue == null ? MIXED_VALUE_INPUT_TEXT : String(commonValue)
}

/**
 * Stable token that changes when the eligible set or any of their committed values change, but
 * NOT on unrelated re-renders — the panels key their "resync the field from the document" effect
 * on it so partial text input is never clobbered mid-typing.
 */
export function effectSyncToken(
  selected: readonly ShapeData[],
  opts: Pick<EffectSelectionOptions, 'isEligible' | 'readValue' | 'liveShapes'>
): string {
  const { isEligible, readValue, liveShapes } = opts

  return selected
    .filter((shape) => isEligible(shape))
    .map((shape) => `${shape.id}:${readValue(resolveLiveShape(shape, liveShapes))}`)
    .join('|')
}
